import { BlogSummaryCard } from "@/components/frontend/entity/blog";
import { Pagination } from "@/components/frontend/ui/pagination";
import type { CardVariant } from "@/components/frontend/ui/card";
import type { ButtonVariant } from "@/components/frontend/ui/button";
import type { BadgeVariant } from "@/components/frontend/ui/badge/badge";
import { cn } from "@/lib/utils";
import { DEFAULT_BLOG_SORT, type QueryBlogsResult, type SortValue } from "./types";
import type { BlogRoutes } from "@/lib/types/blog";
import { resolveRelationshipId } from "@/lib/utils/theme-configuration-class";

export interface BlogOverviewResultsProps {
	basePath: string;
	columns: string;
	blogCardVariant?: CardVariant;
	badgeVariant?: BadgeVariant;
	paginationButtonVariant?: ButtonVariant;
	blogRoutes?: BlogRoutes;
	result: QueryBlogsResult;
	query: string;
	sort: SortValue;
	pending?: boolean;
	onPageChange?: (page: number) => void;
	resultsSummary: string;
	noResultsTitle: string;
	noResultsDescription: string;
}

export function BlogOverviewResults({
	basePath,
	columns,
	result,
	query,
	sort,
	blogCardVariant,
	badgeVariant,
	paginationButtonVariant,
	blogRoutes,
	pending = false,
	onPageChange,
	resultsSummary,
	noResultsTitle,
	noResultsDescription,
}: BlogOverviewResultsProps) {
	const params = new URLSearchParams();
	if (query.trim()) params.set("q", query.trim());
	if (sort !== DEFAULT_BLOG_SORT) params.set("sort", sort);

	return (
		<>
			<p className={cn("mb-8 text-sm text-muted-foreground", pending && "opacity-70")}>{resultsSummary}</p>

			{result.docs.length > 0 ? (
				<ol
					className={cn(
						"grid gap-6 sm:grid-cols-2",
						columns === "4" ? "xl:grid-cols-4" : columns === "2" ? "xl:grid-cols-2" : "xl:grid-cols-3",
						pending && "opacity-70",
					)}
				>
					{result.docs.map((post) => (
						<li key={post.id}>
							<BlogSummaryCard
								post={post}
								variant={blogCardVariant}
								badgeVariant={badgeVariant}
								urlBase={blogRoutes?.[resolveRelationshipId(post.blogType)]?.detailUrlBase ?? ""}
							/>
						</li>
					))}
				</ol>
			) : (
				<div className="py-12 text-center">
					<h3>{noResultsTitle}</h3>
					<p className="mt-2 text-sm text-muted-foreground">{noResultsDescription}</p>
				</div>
			)}

			{result.totalPages > 1 && (
				<Pagination
					className="mt-8 lg:mt-12"
					data={{ meta: { current_page: result.page, last_page: result.totalPages, per_page: 0, total: result.totalDocs } }}
					variant={paginationButtonVariant}
					pagePath={{ basePath, query: params.toString() }}
					onPageChange={onPageChange}
				/>
			)}
		</>
	);
}
